import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { analyticsAPI, widgetsAPI, AnalyticsOverview, Widget } from '../lib/api';
import {
  MessageSquare,
  Users,
  Eye,
  Send,
  Plus,
  ArrowRight,
  Loader2,
} from 'lucide-react';

export default function DashboardPage() {
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [analyticsData, widgetsData] = await Promise.all([
        analyticsAPI.overview(7),
        widgetsAPI.list(),
      ]);
      setAnalytics(analyticsData);
      setWidgets(widgetsData.widgets);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const stats = [
    {
      name: 'Total Page Views',
      value: analytics?.totals.page_views || 0,
      icon: Eye,
      color: 'bg-blue-500',
    },
    {
      name: 'Widget Opens',
      value: analytics?.totals.widget_opens || 0,
      icon: MessageSquare,
      color: 'bg-green-500',
    },
    {
      name: 'Messages Sent',
      value: analytics?.totals.messages_sent || 0,
      icon: Send,
      color: 'bg-purple-500',
    },
    {
      name: 'Unique Sessions',
      value: analytics?.totals.unique_sessions || 0,
      icon: Users,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Overview of your chat widgets performance
          </p>
        </div>
        <Link
          to="/widgets/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Widget
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="bg-white rounded-xl border border-gray-200 p-6"
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}
              >
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stat.value.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600">{stat.name}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Widgets Section */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Your Widgets</h2>
          <Link
            to="/widgets"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            View all
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {widgets.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No widgets yet
            </h3>
            <p className="text-gray-600 mb-6">
              Create your first chat widget to get started
            </p>
            <Link
              to="/widgets/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Widget
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {widgets.slice(0, 5).map((widget) => (
              <Link
                key={widget.id}
                to={`/widgets/${widget.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: widget.theme_config.primaryColor }}
                  >
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{widget.name}</p>
                    <p className="text-sm text-gray-500">
                      {widget.domain_count || 0} domain
                      {(widget.domain_count || 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      widget.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {widget.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Top Widgets by Messages */}
      {analytics && analytics.widgets.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Top Widgets by Messages (Last 7 days)
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {analytics.widgets.slice(0, 5).map((widget, index) => {
                const maxMessages = analytics.widgets[0]?.messages_sent || 1;
                const percentage = (widget.messages_sent / maxMessages) * 100;

                return (
                  <div key={widget.id} className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-500 w-6">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {widget.name}
                        </span>
                        <span className="text-sm text-gray-600">
                          {widget.messages_sent} messages
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
